import { useEffect, useState } from "react";
import { api } from "../lib/api";
import type { ImageRecord } from "../lib/types";

const ASPECTS = ["1:1", "16:9", "9:16", "4:3", "3:4"];

export default function ImageTab({
  initialPrompt,
  onAnimate,
  onError,
}: {
  initialPrompt: string | null;
  onAnimate: (rec: ImageRecord) => void;
  onError: (e: unknown) => void;
}) {
  const [prompt, setPrompt] = useState(initialPrompt ?? "");
  const [aspect, setAspect] = useState("1:1");
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState<ImageRecord[]>([]);

  useEffect(() => {
    if (initialPrompt) setPrompt(initialPrompt);
  }, [initialPrompt]);

  useEffect(() => {
    api.history().then((h) => setRecent(h.images.slice(0, 12))).catch(onError);
  }, [onError]);

  async function generate() {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      const imgs = await api.generateImage(prompt.trim(), { aspect_ratio: aspect });
      setRecent((r) => [...imgs, ...r].slice(0, 24));
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="h-full flex flex-col p-4 gap-4 overflow-y-auto">
      <div className="card p-4 flex flex-col gap-3">
        <label className="text-xs text-zinc-400">Prompt</label>
        <textarea
          className="input resize-none"
          rows={4}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="A neon-lit alley in Tokyo at dusk, cinematic 35mm…"
        />
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-zinc-400 mr-1">Aspect</span>
          {ASPECTS.map((a) => (
            <button
              key={a}
              className={`chip ${aspect === a ? "ring-1 ring-violet-500 text-white" : ""}`}
              onClick={() => setAspect(a)}
            >
              {a}
            </button>
          ))}
          <button className="btn-primary ml-auto" onClick={generate} disabled={busy || !prompt.trim()}>
            {busy ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {recent.map((img) => (
          <div key={img.id} className="card overflow-hidden flex flex-col">
            <img src={img.url} alt={img.prompt} className="w-full aspect-square object-cover bg-zinc-950" loading="lazy" />
            <div className="p-3 text-xs text-zinc-400 flex flex-col gap-2 flex-1">
              <div className="line-clamp-3 text-zinc-300">{img.prompt}</div>
              <div className="flex gap-2 mt-auto">
                <button className="btn-secondary text-xs" onClick={() => onAnimate(img)}>
                  Animate
                </button>
                <a className="btn-ghost text-xs" href={img.url} target="_blank" rel="noreferrer">
                  Open
                </a>
              </div>
            </div>
          </div>
        ))}
        {!busy && recent.length === 0 && (
          <div className="text-zinc-500 text-sm">No images yet. Generate one above.</div>
        )}
      </div>
    </div>
  );
}
